import { AlertTriangle, FileText, Search } from 'lucide-react';
import { readVerdictReport } from '@/modules/review/report-data';
import { GapReportRow, RcaSection } from '@/modules/review/report/gap-report-layout';
import { LadderRail } from '@/modules/review/report/ladder-rail';
import { RUNG_LABELS } from '@/modules/review/rungs';
import { Chip } from '@/modules/review/report/report-bits';
import { PillButton } from '../ui/pill-button';
import { humanize } from '../ui/text';
import { InvestigationSteps } from './investigation-steps';
import { ReviewInvite } from './review-invite';
import type { InvestigationStep } from './review.store';

interface GapInvestigationViewProps {
  investigation?: unknown;
  running: boolean;
  steps: InvestigationStep[];
  failure?: string | null;
  canInvestigate: boolean;
  onInvestigate: () => void;
  onOpenReport: () => void;
}

/** The same RCA layout as live-ticket gaps, showing this run's evidence and suggestions. */
export function GapInvestigationView({
  investigation, running, steps, failure, canInvestigate, onInvestigate, onOpenReport,
}: GapInvestigationViewProps) {
  const report = readVerdictReport(investigation);
  if (running) return <InvestigationSteps steps={steps} live />;
  if (failure) {
    return (
      <div className='flex flex-col gap-3'>
        <p className='flex items-center gap-2 text-body-sm text-amber-900'>
          <AlertTriangle className='size-4 shrink-0 text-amber-600' aria-hidden />
          The investigation did not finish ({failure}).
        </p>
        <PillButton onClick={onInvestigate} disabled={!canInvestigate}>Try again</PillButton>
      </div>
    );
  }
  if (!report) {
    return canInvestigate ? (
      <ReviewInvite icon={Search} title='Investigate this run' action='Investigate' onAction={onInvestigate}
        description='Read the trace to understand what happened, with or without a Shadow comparison.' />
    ) : (
      <p className='text-xs leading-4 text-gray-800'>This run carries no trace, so there is nothing for the investigator to read.</p>
    );
  }
  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          {report.disposition && <Chip tone='outline'>{humanize(report.disposition)}</Chip>}
          {report.severity && report.severity !== 'none' && <Chip tone='outline'>{humanize(report.severity)}</Chip>}
        </div>
      </div>
      <RcaSection title='Gap report'>
        <div className='flex flex-col gap-1.5 pl-1.5'>
          <GapReportRow label='Expected'>{report.expected ?? 'Not recorded.'}</GapReportRow>
          <GapReportRow label='Actual'>{report.actual ?? 'Not recorded.'}</GapReportRow>
          {report.rootCause && <GapReportRow label='Root cause'>{report.rootCause}</GapReportRow>}
          <GapReportRow label='First causal breakdown'>
            {report.firstBrokenRung
              ? RUNG_LABELS.find((rung) => rung.key === report.firstBrokenRung)?.label ?? humanize(report.firstBrokenRung)
              : 'No first causal breakdown recorded.'}
            {report.ladder.length > 0 && <div className='overflow-x-auto py-3'><LadderRail ladder={report.ladder} /></div>}
          </GapReportRow>
          <GapReportRow label='Cause chain'>
            {report.causeChain.length ? report.causeChain.map((step, index) => <p key={index}>{step}</p>) : 'Not recorded.'}
          </GapReportRow>
          <GapReportRow label='Evidence'>
            {report.evidence.length ? report.evidence.map((item, index) => (
              <div key={index} className='mb-2 last:mb-0'>
                {item.ref && <span className='font-mono text-micro text-gray-800'>{item.ref}</span>}
                <p>{item.observation}</p>
              </div>
            )) : 'No trace evidence recorded.'}
          </GapReportRow>
        </div>
      </RcaSection>
      <div className='border-t border-gray-500 pt-8'>
        <RcaSection title='Suggested fixes'>
          {report.fixes.length ? (
            <ol className='flex flex-col gap-2'>
              {report.fixes.map((fix, index) => (
                <li key={index} className='flex gap-3 rounded-lg border border-gray-500 p-3 text-body-sm text-text-body'>
                  <span className='text-gray-800'>{index + 1}.</span>
                  <div className='flex flex-col gap-2'>
                    <p>{fix.text}</p>
                    {fix.targetType && <Chip tone='outline'>{humanize(fix.targetType)}</Chip>}
                  </div>
                </li>
              ))}
            </ol>
          ) : <p className='text-body-sm text-gray-900'>No suggested fixes recorded.</p>}
        </RcaSection>
      </div>
      <PillButton variant='outline' className='w-fit gap-1.5' onClick={onOpenReport}>
        <FileText className='size-3.5' aria-hidden />View report
      </PillButton>
    </div>
  );
}
